import { useMemo, useState } from 'react'
import { Tag } from '../ui'
import {
  DemoHeader,
  InsightCard,
  OntologyResponsePanel,
  UserActionPanel,
} from './GoalDrivenDemo'
import { useT } from '../../lib/i18n'
import type { LabDemoProps } from './types'
import type { RiskScoringLocale } from '../../lib/locales'

type DimensionId = 'financial' | 'operational' | 'esg'
type Band = 'safe' | 'watch' | 'critical'

function classify(score: number, thresholds: RiskScoringLocale['thresholds']): Band {
  if (score <= thresholds.safe.upper) return 'safe'
  if (score <= thresholds.watch.upper) return 'watch'
  return 'critical'
}

export function RiskScoringDemo({ paradigm, lab, data }: LabDemoProps) {
  const t = useT()
  const demo = (data as typeof t.labDemos.riskScoring | undefined) ?? t.labDemos.riskScoring

  const defaults = useMemo<Record<DimensionId, number>>(() => {
    const d: Record<DimensionId, number> = { financial: 0, operational: 0, esg: 0 }
    demo.dimensions.forEach((dim) => {
      d[dim.id] = dim.defaultValue
    })
    return d
  }, [demo.dimensions])

  const [values, setValues] = useState<Record<DimensionId, number>>(defaults)

  const contributions = demo.dimensions.map((dim) => {
    const v = values[dim.id]
    const contribution = (v / 10) * dim.weightPct
    return { ...dim, value: v, contribution }
  })

  const totalScore = contributions.reduce((acc, c) => acc + c.contribution, 0)
  const band = classify(totalScore, demo.thresholds)
  const isDirty =
    values.financial !== defaults.financial ||
    values.operational !== defaults.operational ||
    values.esg !== defaults.esg

  return (
    <div className="space-y-5">
      <DemoHeader paradigm={paradigm} />

      <div className="flex flex-col gap-4">
        <UserActionPanel
          label={lab.userActionLabel}
          title={demo.controlLabel}
          hint={demo.controlHint}
        >
          <div className="flex items-center justify-between gap-3 flex-wrap mb-4">
            <SupplierContext context={demo.contextSupplier} />
            {isDirty && (
              <button
                type="button"
                onClick={() => setValues(defaults)}
                className="mono text-[length:var(--text-micro)] uppercase tracking-widest px-3 py-1.5 rounded-[var(--radius-sm)] border-faint text-[color:var(--color-orion-haze)] hover:text-[color:var(--color-orion-text)] hover:bg-[color:var(--color-orion-night)] transition-colors"
              >
                {demo.resetButton}
              </button>
            )}
          </div>

          <div className="grid sm:grid-cols-3 gap-4">
            {demo.dimensions.map((dim) => (
              <DimensionSlider
                key={dim.id}
                label={dim.label}
                description={dim.description}
                weightPct={dim.weightPct}
                value={values[dim.id]}
                onChange={(v) => setValues((curr) => ({ ...curr, [dim.id]: v }))}
              />
            ))}
          </div>
        </UserActionPanel>

        <OntologyResponsePanel label={lab.ontologyResponseLabel}>
          <div className="grid lg:grid-cols-[1fr_320px] gap-5 items-start">
            <ScoreCard
              score={totalScore}
              caption={demo.scoreCaption}
              title={demo.scoreTitle}
              band={band}
              thresholds={demo.thresholds}
            />
            <AttributionPanel
              title={demo.attributionTitle}
              hint={demo.attributionHint}
              formula={demo.contributionFormula}
              contributions={contributions}
            />
          </div>
        </OntologyResponsePanel>
      </div>

      <InsightCard title={demo.insightTitle} body={demo.insightBody} />
    </div>
  )
}

function SupplierContext({ context }: { context: RiskScoringLocale['contextSupplier'] }) {
  return (
    <div className="flex items-baseline gap-3 flex-wrap">
      <span className="mono text-[length:var(--text-micro)] uppercase tracking-widest text-[color:var(--color-orion-haze)]">
        {context.label}
      </span>
      <span
        className="text-[color:var(--color-orion-text)] font-medium"
        style={{ fontSize: 'var(--text-body)' }}
      >
        {context.name}
      </span>
      <span
        className="text-[color:var(--color-orion-haze)]"
        style={{ fontSize: 'var(--text-small)' }}
      >
        {context.detail}
      </span>
    </div>
  )
}

function DimensionSlider({
  label,
  description,
  weightPct,
  value,
  onChange,
}: {
  label: string
  description: string
  weightPct: number
  value: number
  onChange: (v: number) => void
}) {
  return (
    <div className="border-faint rounded-[var(--radius-md)] p-3 bg-[color:var(--color-orion-deep)]/30">
      <div className="flex items-baseline justify-between gap-2 mb-1">
        <span
          className="text-[color:var(--color-orion-text)] font-medium"
          style={{ fontSize: 'var(--text-small)' }}
        >
          {label}
        </span>
        <span className="mono text-[length:var(--text-micro)] uppercase tracking-widest text-[color:var(--color-orion-amber)]">
          {weightPct}%
        </span>
      </div>
      <p
        className="text-[color:var(--color-orion-haze)] mb-2 leading-snug"
        style={{ fontSize: 'var(--text-micro)' }}
      >
        {description}
      </p>
      <div className="flex items-center gap-2">
        <input
          type="range"
          min={0}
          max={10}
          step={0.5}
          value={value}
          onChange={(e) => onChange(Number(e.target.value))}
          aria-label={label}
          className="flex-1 accent-[color:var(--color-orion-amber)]"
        />
        <span className="mono text-[length:var(--text-small)] text-[color:var(--color-orion-amber)] font-medium w-8 text-right">
          {value.toFixed(1)}
        </span>
      </div>
    </div>
  )
}

function ScoreCard({
  score,
  caption,
  title,
  band,
  thresholds,
}: {
  score: number
  caption: string
  title: string
  band: Band
  thresholds: RiskScoringLocale['thresholds']
}) {
  const bandLabel =
    band === 'safe'
      ? thresholds.safe.label
      : band === 'watch'
        ? thresholds.watch.label
        : thresholds.critical.label
  const bandColor =
    band === 'safe'
      ? 'var(--color-orion-pass)'
      : band === 'watch'
        ? 'var(--color-orion-warn)'
        : 'var(--color-orion-block)'

  return (
    <div className="border-faint rounded-[var(--radius-md)] p-5 bg-[color:var(--color-orion-deep)]/30">
      <div className="mono text-[length:var(--text-micro)] uppercase tracking-widest text-[color:var(--color-orion-amber)] mb-2">
        {title}
      </div>

      <div className="flex items-baseline gap-3 mb-1">
        <span
          className="mono font-light"
          style={{ fontSize: '64px', lineHeight: 1, color: bandColor }}
        >
          {score.toFixed(1)}
        </span>
        <span className="mono text-[color:var(--color-orion-haze)]">/ 100</span>
      </div>
      <div className="mono text-[length:var(--text-micro)] uppercase tracking-widest text-[color:var(--color-orion-haze)] mb-4">
        {caption}
      </div>

      <div className="relative h-3 rounded-full overflow-hidden mb-2">
        <div className="absolute inset-0 flex">
          <div
            style={{
              width: `${thresholds.safe.upper}%`,
              background: 'color-mix(in oklch, var(--color-orion-pass) 60%, transparent)',
            }}
          />
          <div
            style={{
              width: `${thresholds.watch.upper - thresholds.safe.upper}%`,
              background: 'color-mix(in oklch, var(--color-orion-warn) 60%, transparent)',
            }}
          />
          <div
            style={{
              width: `${100 - thresholds.watch.upper}%`,
              background: 'color-mix(in oklch, var(--color-orion-block) 60%, transparent)',
            }}
          />
        </div>
        <div
          className="absolute top-[-4px] bottom-[-4px] w-[3px] rounded-full transition-[left] duration-200"
          style={{
            left: `calc(${Math.min(100, Math.max(0, score))}% - 1.5px)`,
            background: 'var(--color-orion-text)',
            boxShadow: '0 0 0 2px var(--color-orion-deep)',
          }}
        />
      </div>
      <div className="flex justify-between mono text-[length:var(--text-micro)] text-[color:var(--color-orion-haze)]">
        <span>0</span>
        <span>{thresholds.safe.upper}</span>
        <span>{thresholds.watch.upper}</span>
        <span>100</span>
      </div>

      <div className="mt-4">
        <Tag
          kind={band === 'safe' ? 'standard' : 'rule'}
          className="text-[length:var(--text-micro)]"
        >
          {bandLabel}
        </Tag>
      </div>
    </div>
  )
}

function AttributionPanel({
  title,
  hint,
  formula,
  contributions,
}: {
  title: string
  hint: string
  formula: string
  contributions: { id: DimensionId; label: string; weightPct: number; value: number; contribution: number }[]
}) {
  return (
    <aside
      className="border-faint rounded-[var(--radius-md)] p-4"
      style={{ borderLeft: '2px solid color-mix(in oklch, var(--color-orion-cyan) 60%, transparent)' }}
    >
      <div className="mono text-[length:var(--text-micro)] uppercase tracking-widest text-[color:var(--color-orion-cyan)] mb-2">
        {title}
      </div>
      <p
        className="text-[color:var(--color-orion-haze)] mb-3 leading-snug"
        style={{ fontSize: 'var(--text-micro)' }}
      >
        {hint}
      </p>
      <ul className="space-y-2.5">
        {contributions.map((c) => (
          <li key={c.id}>
            <div className="flex items-baseline justify-between gap-2 mb-0.5">
              <span
                className="text-[color:var(--color-orion-text)]"
                style={{ fontSize: 'var(--text-small)' }}
              >
                {c.label}
              </span>
              <span className="mono text-[length:var(--text-small)] text-[color:var(--color-orion-amber)] font-medium">
                {c.contribution.toFixed(1)}
              </span>
            </div>
            <div className="mono text-[length:var(--text-micro)] text-[color:var(--color-orion-haze)] mb-1">
              {formula
                .replace('{value}', c.value.toFixed(1))
                .replace('{weight}', c.weightPct.toString())
                .replace('{contribution}', c.contribution.toFixed(1))}
            </div>
            <div className="h-1 rounded-full bg-[color:var(--color-orion-surface)]/30 overflow-hidden">
              <div
                className="h-full bg-[color:var(--color-orion-amber)] transition-[width] duration-200"
                style={{ width: `${(c.contribution / c.weightPct) * 100}%` }}
              />
            </div>
          </li>
        ))}
      </ul>
    </aside>
  )
}
